import { eachDayOfInterval, format, parseISO } from "date-fns";
import type {
  AttendanceListResponse,
  ClockingCell,
  ClockingGridModel,
  ClockingGroup,
  ClockingRow,
  TimeOffApprovalStatus,
} from "../types";

function enumerateDates(start: string, end: string): string[] {
  try {
    return eachDayOfInterval({
      start: parseISO(start),
      end: parseISO(end),
    }).map((d) => format(d, "yyyy-MM-dd"));
  } catch {
    return [];
  }
}

function mapCell(raw: AttendanceListResponse["groups"][0]["rows"][0]["cells"][0]): ClockingCell {
  const approval = raw.time_off_approval_status;
  return {
    date: raw.date,
    attendanceId: raw.attendance_id,
    timeOffId: raw.time_off_id,
    timeOffApprovalStatus:
      approval === 1 || approval === 2 || approval === 3 ? (approval as TimeOffApprovalStatus) : null,
    displayStatus: raw.display_status,
    displayStatusLabel: raw.display_status_label,
    status: raw.status,
    statusLabel: raw.status_label,
    clockInAt: raw.clock_in_at,
    clockOutAt: raw.clock_out_at,
  };
}

function emptyCell(date: string): ClockingCell {
  return {
    date,
    attendanceId: null,
    timeOffId: null,
    timeOffApprovalStatus: null,
    displayStatus: null,
    displayStatusLabel: null,
    status: null,
    statusLabel: null,
    clockInAt: null,
    clockOutAt: null,
  };
}

function mapRow(raw: AttendanceListResponse["groups"][0]["rows"][0], dates: string[]): ClockingRow {
  const byDate = new Map(raw.cells.map((c) => [c.date, mapCell(c)]));
  return {
    resourceId: raw.resource_id,
    name: raw.resource_name,
    role: raw.role,
    cells: dates.map((date) => byDate.get(date) ?? emptyCell(date)),
  };
}

function groupKey(projectId: number | null, projectName: string): string {
  if (projectId == null || projectName === "No Task") return "no-task";
  return `project-${projectId}`;
}

function workDateKey(workDate: string): string {
  return workDate.slice(0, 10);
}

export function mapAttendanceResponse(data: AttendanceListResponse): ClockingGridModel {
  const start = data.date_range?.start ?? "";
  const end = data.date_range?.end ?? "";
  const dates = enumerateDates(start, end);

  const groups: ClockingGroup[] = (data.groups ?? []).map((g) => ({
    key: groupKey(g.project_id, g.project_name),
    projectId: g.project_id,
    title: g.project_name || "No Task",
    subtitle: g.location_name || "",
    workerCount: g.worker_count ?? g.rows?.length ?? 0,
    rows: (g.rows ?? []).map((r) => mapRow(r, dates)),
  }));

  return {
    dateRange: { start, end },
    dates,
    legend: data.status_legend ?? [],
    groups,
  };
}

/** Patch a single attendance detail into the grid model (websocket / after action). */
export function patchAttendanceIntoModel(
  model: ClockingGridModel,
  detail: {
    id: number;
    resource: number;
    work_date: string;
    display_status: string;
    display_status_label: string;
    status: number;
    status_label: string;
    clock_in_at: string | null;
    clock_out_at: string | null;
  }
): ClockingGridModel {
  const workDate = workDateKey(detail.work_date);

  const groups = model.groups.map((group) => ({
    ...group,
    rows: group.rows.map((row) => {
      const resourceMatches = Number(row.resourceId) === Number(detail.resource);
      const rowHasAttendance = row.cells.some((cell) => cell.attendanceId === detail.id);
      if (!resourceMatches && !rowHasAttendance) return row;

      return {
        ...row,
        cells: row.cells.map((cell) => {
          const matchesId = cell.attendanceId === detail.id;
          const matchesDateSlot =
            cell.date === workDate &&
            (cell.attendanceId == null || cell.attendanceId === detail.id);
          if (!matchesId && !matchesDateSlot) return cell;
          return {
            ...cell,
            attendanceId: detail.id,
            displayStatus: detail.display_status ?? cell.displayStatus,
            displayStatusLabel: detail.display_status_label ?? cell.displayStatusLabel,
            status: detail.status ?? cell.status,
            statusLabel: detail.status_label ?? cell.statusLabel,
            clockInAt: detail.clock_in_at,
            clockOutAt: detail.clock_out_at,
          };
        }),
      };
    }),
  }));

  return { ...model, groups };
}
